import { selectFeatureFlags } from 'core/userContext';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  CustomEventFormProvider,
  UPDATE_CUSTOM_EVENT_SETTINGS_ACTION,
  useCustomEventsQueryContext,
  useModalsContext,
} from '../contexts';
import { INGESTION_POLICIES } from '../types';
import { AllowNewEventsModal } from './AllowNewEventsModal';
import { BlockUnknownEventsModal } from './BlockUnknownEventsModal';
import { IngestionPolicyModals } from './IngestionPolicyModals';
import { NewCustomEventModal } from './NewCustomEventModal';
import { RetentionPeriodUpdateModal } from './RetentionPeriodUpdateModal';

type Props = {
  setIngestionPolicyFilter: (ingestionPolicy: INGESTION_POLICIES) => void;
};

export const UpdateCustomEventSettingsModals = ({ setIngestionPolicyFilter }: Props) => {
  const { modalState, hideModal: onHideModal } = useModalsContext();
  const { customEvents, refetchCustomEventMappings } = useCustomEventsQueryContext();
  const featureFlags = useSelector(selectFeatureFlags);

  const existingEvents = useMemo(
    () => customEvents.map((event) => event.eventName),
    [customEvents],
  );

  const hideModal = () => {
    refetchCustomEventMappings();
    onHideModal();
  };

  if (modalState === null) return null;

  const { eventNames, action } = modalState;

  switch (action) {
    case UPDATE_CUSTOM_EVENT_SETTINGS_ACTION.ADD:
      return (
        <CustomEventFormProvider existingEventNames={existingEvents} featureFlags={featureFlags}>
          <NewCustomEventModal onClose={hideModal} />
        </CustomEventFormProvider>
      );
    case UPDATE_CUSTOM_EVENT_SETTINGS_ACTION.ALLOW:
      return <AllowNewEventsModal onClose={hideModal} />;
    case UPDATE_CUSTOM_EVENT_SETTINGS_ACTION.BLOCK:
      return <BlockUnknownEventsModal onClose={hideModal} />;
    case UPDATE_CUSTOM_EVENT_SETTINGS_ACTION.UPDATE_RETENTION_PERIOD:
      return <RetentionPeriodUpdateModal eventNames={eventNames} onClose={hideModal} />;
    case UPDATE_CUSTOM_EVENT_SETTINGS_ACTION.SAVE:
    case UPDATE_CUSTOM_EVENT_SETTINGS_ACTION.DISCARD:
    case UPDATE_CUSTOM_EVENT_SETTINGS_ACTION.DEACTIVATE:
      return (
        <IngestionPolicyModals
          action={action}
          eventName={eventNames[0]}
          onClose={hideModal}
          setIngestionPolicyFilter={setIngestionPolicyFilter}
        />
      );
    default:
      return null;
  }
};
